import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid2,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  IconButton,
  TextField,
} from "@mui/material";
import { CourseInstance } from "../model/entity/CourseInstance";
import { Coach } from "../model/entity/Coach";
import { useEffect, useRef, useState } from "react";
import { Loading } from "./Loading";
import dayjs from "dayjs";
import { notifErr, notifOk } from "./Functions";
import handleAxiosError from "./ErrorHandler";
import { putFetcher } from "./Fetchers";
import { Delete, ChangeCircle } from "@mui/icons-material";

interface SubstitutionDialogProps {
  open: boolean;
  selectedInstance: CourseInstance;
  courseInstances: CourseInstance[];
  coaches: Coach[];
  handleCancel: any;
}

const SubstitutionDialog = (props: SubstitutionDialogProps) => {
  const { coaches } = { ...props };
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const [coachInputVal, setCoachInputVal] = useState("");
  const [currentInstance, setCurrentInstance] = useState<CourseInstance | null>(
    {
      ...props.selectedInstance,
    }
  );
  const [copiedCourseInstances, setCopiedCourseInstances] = useState<
    CourseInstance[] | null
  >([...props.courseInstances]);

  const myRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setTimeout(() => {
      myRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  }, [currentInstance]);

  const filterOnlyChanged = (arr: CourseInstance[]) => {
    return arr.filter((ci) => {
      const oldCi = props.courseInstances.find((oldCi) => oldCi.id === ci.id)!;
      return (
        ci.canceled !== oldCi.canceled ||
        ci.coachIds.length !== oldCi.coachIds.length ||
        ci.coachIds.some((id) => !oldCi.coachIds.includes(id))
      );
    });
  };

  const handleAddCoach = (coach: Coach | null) => {
    if (!currentInstance || !coach) return;

    const updatedArr: number[] = [...currentInstance.coachIds, coach.id!];
    setCurrentInstance({ ...currentInstance, coachIds: updatedArr });
    setCoachInputVal("");
    setSelectedCoach(null);
  };

  const handleRemoveCoach = (coach: Coach | undefined) => {
    if (!currentInstance || !coach) return;

    const updatedArr: number[] = currentInstance.coachIds.filter(
      (id) => id !== coach.id!
    );
    setCurrentInstance({ ...currentInstance, coachIds: updatedArr });
  };

  const handleSelectedInstance = (newInstance: CourseInstance) => {
    if (
      !currentInstance ||
      !copiedCourseInstances ||
      newInstance.id === currentInstance.id
    )
      return;
    const arr: CourseInstance[] = copiedCourseInstances.map((instance) =>
      instance.id === currentInstance.id ? currentInstance : instance
    );
    setCopiedCourseInstances([...arr]);
    setCurrentInstance(newInstance);
  };

  const handleSubmit = async () => {
    if (!currentInstance || !copiedCourseInstances) return;
    const arr: CourseInstance[] = copiedCourseInstances.map((instance) =>
      instance.id === currentInstance.id ? currentInstance : instance
    );

    const onlyChangedArr = filterOnlyChanged(arr);

    try {
      await putFetcher(
        `${process.env.REACT_APP_API_URL}/course-instances`,
        onlyChangedArr
      );
      notifOk("Změny byly uloženy.");
      props.handleCancel();
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  if (!currentInstance || !copiedCourseInstances || !coaches)
    return <Loading />;

  return (
    <Dialog open={props.open} onClose={() => props.handleCancel()}>
      <DialogContent dividers>
        <Grid2 container>
          <Grid2 size={{ xs: 9 }}>
            <Typography variant="h5">
              {currentInstance.course.name +
                " " +
                currentInstance.startTime.substring(0, 5) +
                "-" +
                currentInstance.endTime.substring(0, 5)}
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 9 }}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    color: "red",
                    "&.Mui-checked": {
                      color: "red",
                    },
                  }}
                />
              }
              label="Zrušeno v tento den"
              checked={currentInstance.canceled}
              onChange={() =>
                setCurrentInstance({
                  ...currentInstance,
                  canceled: !currentInstance.canceled,
                })
              }
              sx={{ color: "red" }}
            />
          </Grid2>
          <Grid2 size={{ xs: 9 }}>
            <Autocomplete
              size="small"
              value={selectedCoach}
              inputValue={coachInputVal}
              sx={{ width: "45%", my: "0.5rem", ml: "7.8rem" }}
              options={coaches.filter(
                (c) => !currentInstance.coachIds.includes(c.id!)
              )}
              getOptionKey={(coach) => coach.id!}
              getOptionLabel={(coach) => coach.firstName + " " + coach.lastName}
              filterOptions={(o, { inputValue }) =>
                o.filter((coach) =>
                  (coach.firstName + " " + coach.lastName)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Přiřadit trenéra"
                  placeholder="Hledat..."
                />
              )}
              onChange={(e, val) => handleAddCoach(val)}
              onInputChange={(e, val) => setCoachInputVal(val)}
            />
            <Table
              size="small"
              sx={{
                width: "75%",
                ml: "4rem",
                height: "18.5rem",
                overflowY: "auto",
                scrollbarWidth: "thin",
                display: "block",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <TableBody>
                {currentInstance.coachIds.map((coachId, index) => {
                  const coach = coaches.find((c) => c.id === coachId);

                  return (
                    <TableRow key={index}>
                      <TableCell className="tableCell tableFirstCell">
                        {coach?.firstName + " " + coach?.lastName}
                        {!currentInstance.course.coachLeadsCourses.some(
                          (clc) => clc.coach.id === coachId
                        ) && (
                          <ChangeCircle
                            sx={{
                              color: "var(--gradientStart)",
                              fontSize: "1.1rem",
                              ml: "0.1rem",
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ width: "5%", textAlign: "right" }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveCoach(coach)}
                        >
                          <Delete sx={{ color: "var(--orangeWarning)" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Grid2>
          <Grid2
            size={{ xs: 3 }}
            sx={{
              display: "flex",
              justifyContent: "right",
              alignItems: "flex-end",
              flexDirection: "column",
            }}
          >
            <Table
              sx={{
                height: "22rem",
                overflowY: "auto",
                scrollbarWidth: "thin",
                display: "block",
                width: "auto",
                textAlign: "right",
                userSelect: "none",
                border: "1px solid var(--muiBorderGray)",
              }}
            >
              <TableBody>
                {copiedCourseInstances.map((ci) => (
                  <TableRow key={ci.id}>
                    <TableCell
                      ref={ci.id === currentInstance.id ? myRef : null}
                      onClick={() => handleSelectedInstance(ci)}
                      className={`courseInstanceListCell ${
                        ci.id === currentInstance.id
                          ? "courseInstanceListSelectedCell"
                          : ""
                      }`}
                    >
                      {dayjs(ci.date).format("D.M.")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid2>
        </Grid2>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "right", gap: 1 }}>
        <Button
          variant="outlined"
          className="cancelBtn"
          onClick={props.handleCancel}
        >
          Zrušit
        </Button>
        <Button variant="outlined" className="submitBtn" onClick={handleSubmit}>
          Uložit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubstitutionDialog;
